import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  CircularProgress,
  InputAdornment,
  IconButton,
  Card,
  CardContent,
  Stack,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import sucursalesVentaService, { SucursalVenta } from '../services/sucursalesVentaService';

/** Referencia estable para evitar re-fetch en loop cuando no se excluyen sucursales. */
const EMPTY_EXCLUDE: number[] = [];

interface SucursalVentaSearchProps {
  open: boolean;
  onClose: () => void;
  onSelect: (sucursal: SucursalVenta) => void;
  excludeNroSuc?: number[];
  /** Si se indica, solo muestra sucursales de esa compañía. */
  idCompania?: string;
}

const SucursalVentaSearch: React.FC<SucursalVentaSearchProps> = ({
  open,
  onClose,
  onSelect,
  excludeNroSuc,
  idCompania,
}) => {
  const excluded = excludeNroSuc ?? EMPTY_EXCLUDE;
  const excludeKey = excluded.length === 0 ? '' : excluded.join(',');
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchTerm, setSearchTerm] = useState('');
  const [sucursales, setSucursales] = useState<SucursalVenta[]>([]);
  const [sucursalesFiltradas, setSucursalesFiltradas] = useState<SucursalVenta[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const excludeSet = useMemo(() => new Set(excluded), [excludeKey]);

  const loadSucursales = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await sucursalesVentaService.getSucursalesVenta({
        pageNumber: 1,
        pageSize: 500,
      });
      const raw = result.items ?? [];
      const idCompaniaFiltro = String(idCompania ?? '').trim();
      const filtered = raw.filter((s) => {
        if (excludeSet.has(s.nroSuc)) return false;
        if (!idCompaniaFiltro) return true;
        return String(s.idCompania ?? '').trim() === idCompaniaFiltro;
      });
      setSucursales(filtered);
      setSucursalesFiltradas(filtered);
      if (filtered.length === 0) {
        setError(
          excluded.length > 0
            ? 'No hay más sucursales disponibles para asignar.'
            : 'No se encontraron sucursales.'
        );
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error desconocido';
      setError(`Error al cargar sucursales: ${message}`);
      setSucursales([]);
      setSucursalesFiltradas([]);
    } finally {
      setLoading(false);
    }
  }, [excludeSet, excludeKey, excluded.length, idCompania]);

  const handleSearch = useCallback(() => {
    const trimmed = searchTerm.trim();
    if (!trimmed) {
      setSucursalesFiltradas(sucursales);
      setError(null);
      return;
    }

    const trimmedLower = trimmed.toLowerCase();
    const filtered = sucursales.filter((s) => {
      const nroSuc = String(s.nroSuc).includes(trimmed);
      const nombre = s.nombreSucursal?.toLowerCase().includes(trimmedLower);
      return nroSuc || nombre;
    });

    setSucursalesFiltradas(filtered);
    if (filtered.length === 0) {
      setError('No se encontraron sucursales.');
    } else {
      setError(null);
    }
  }, [searchTerm, sucursales]);

  const handleSelect = (sucursal: SucursalVenta) => {
    onSelect(sucursal);
    onClose();
  };

  const handleClose = () => {
    setSearchTerm('');
    setSucursalesFiltradas(sucursales);
    setError(null);
    onClose();
  };

  useEffect(() => {
    if (open) {
      loadSucursales();
    } else {
      setSearchTerm('');
      setSucursales([]);
      setSucursalesFiltradas([]);
      setError(null);
    }
  }, [open, loadSucursales]);

  useEffect(() => {
    if (!open) return;
    const trimmed = searchTerm.trim();
    if (!trimmed) {
      setSucursalesFiltradas(sucursales);
      if (sucursales.length > 0) setError(null);
      return;
    }
    const trimmedLower = trimmed.toLowerCase();
    const filtered = sucursales.filter((s) => {
      const nroSuc = String(s.nroSuc).includes(trimmed);
      const nombre = s.nombreSucursal?.toLowerCase().includes(trimmedLower);
      return nroSuc || nombre;
    });
    setSucursalesFiltradas(filtered);
    setError(filtered.length === 0 ? 'No se encontraron sucursales.' : null);
  }, [open, searchTerm, sucursales]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isSmallScreen}
      sx={{
        '& .MuiDialog-paper': {
          margin: { xs: 0, sm: 2 },
          maxHeight: { xs: '100%', sm: '90vh' },
          height: { xs: '100%', sm: 'auto' },
        },
      }}
    >
      <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
            Buscar Sucursal de Venta
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 2 } }}>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Número de sucursal o nombre"
            inputProps={{ 'aria-label': 'Buscar sucursal de venta' }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
            size={isSmallScreen ? 'small' : 'medium'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleSearch} disabled={loading} edge="end">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1.5 }}
          />
        </Box>

        {error && (
          <Typography color="error" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            {error}
          </Typography>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && sucursalesFiltradas.length > 0 && (
          <>
            {isSmallScreen ? (
              <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
                <Stack spacing={2}>
                  {sucursalesFiltradas.map((sucursal) => (
                    <Card
                      key={sucursal.nroSuc}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          boxShadow: 3,
                        },
                      }}
                      onClick={() => handleSelect(sucursal)}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            mb: 1,
                          }}
                        >
                          <Typography variant="subtitle2" color="primary" fontWeight="bold">
                            Nro: {sucursal.nroSuc}
                          </Typography>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelect(sucursal);
                            }}
                            sx={{ minWidth: 'auto', px: 2 }}
                          >
                            Seleccionar
                          </Button>
                        </Box>
                        <Divider sx={{ my: 1.5 }} />
                        <Stack spacing={1}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Nombre
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {sucursal.nombreSucursal?.trim() || '-'}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                sx={{
                  maxHeight: 400,
                  '& .MuiTableCell-root': {
                    py: 1.5,
                    fontSize: '0.875rem',
                  },
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Nro. Sucursal</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 300 }}>Nombre</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: 120 }} align="center">
                        Acción
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sucursalesFiltradas.map((sucursal) => (
                      <TableRow
                        key={sucursal.nroSuc}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleSelect(sucursal)}
                      >
                        <TableCell>{sucursal.nroSuc}</TableCell>
                        <TableCell>{sucursal.nombreSucursal?.trim() || '-'}</TableCell>
                        <TableCell align="center">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelect(sucursal);
                            }}
                          >
                            Seleccionar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}

        {!loading && sucursalesFiltradas.length === 0 && !error && (
          <Typography color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
            No se encontraron sucursales
          </Typography>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          pb: { xs: 2, sm: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
        }}
      >
        <Button
          onClick={handleClose}
          fullWidth={isSmallScreen}
          size={isSmallScreen ? 'medium' : 'small'}
          variant="outlined"
        >
          CANCELAR
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SucursalVentaSearch;
